import { isTokenExpired } from '@/lib/jwt'
import {
  QrCode,
  TrendingUp,
  Users,
  Package,
  Wallet,
  Coins,
  Receipt,
  Building2,
  FileText,
  FileSignature,
  Calendar,
  Bell,
  Lock,
  List,
  Plug,
  CheckSquare,
  CreditCard,
  HandCoins,
  BookOpen,
  BarChart2,
  LayoutDashboard,
  Tag,
  PiggyBank,
  Target,
  Bot,
  type LucideIcon,
} from 'lucide-react'

/**
 * Check if a JWT token is expiring soon (within threshold minutes).
 * Returns false if the token is already expired.
 */
export function isTokenExpiringSoon(token: string, thresholdMinutes = 5): boolean {
  if (isTokenExpired(token)) return false

  try {
    const parts = token.split('.')
    if (parts.length !== 3) return false

    const payload = JSON.parse(atob(parts[1]))
    const exp = (payload.exp as number) * 1000 // Convert to milliseconds
    const now = Date.now()
    const thresholdMs = thresholdMinutes * 60 * 1000

    return exp - now < thresholdMs
  } catch {
    return false
  }
}

/**
 * Sub-route definition for hierarchical navigation.
 * Each module can have multiple child routes that appear
 * as sub-items in the sidebar dropdown.
 */
export interface SubRoute {
  path: string
  label: string
  icon?: LucideIcon
}

/**
 * Map of JWT scope strings to navigation module metadata.
 *
 * IMPORTANT: The `scope` field must match the backend feature_code values
 * stored in TenantFeature table and returned in JWT scopes.
 * Backend uses: qr_attendance, sales_analytics, crm_core, stock
 * (not just 'qr', 'sales', 'crm').
 */
export interface ModuleInfo {
  scope: string
  path: string
  icon: LucideIcon
  label: string
  subscribed?: boolean
  children?: SubRoute[]
  benefits: string[]
}

const MODULE_MAP: Record<string, ModuleInfo> = {
  documentation: {
    scope: 'documentation',
    path: '/documentation',
    icon: BookOpen,
    label: 'Documentación',
    benefits: [],
  },
  qr_attendance: {
    scope: 'qr_attendance',
    path: '/attendance',
    icon: QrCode,
    label: 'Asistencia',
    benefits: [
      'Control de asistencia por QR',
      'Gestión de empleados',
      'Registros de entrada/salida',
      'Alertas de anomalías',
    ],
    children: [
      { path: '/attendance/qr', label: 'QR Display', icon: QrCode },
      { path: '/attendance/employees', label: 'Empleados', icon: Users },
      { path: '/attendance/records', label: 'Jornadas', icon: Calendar },
      { path: '/attendance/alerts', label: 'Alertas', icon: Bell },
      { path: '/attendance/holidays', label: 'Feriados', icon: Calendar },
      { path: '/attendance/payroll', label: 'Nómina', icon: Wallet },
      { path: '/analytics', label: 'Analytics', icon: TrendingUp },
    ],
  },
  sales_analytics: {
    scope: 'sales_analytics',
    path: '/sales',
    icon: TrendingUp,
    label: 'Ventas',
    benefits: [
      'Importá CSVs de ventas',
      'Dashboards con KPIs',
      'Gráficos de evolución diaria/semanal/mensual',
      'Distribución por producto',
      'Comparación entre períodos',
      'Historial de importaciones',
    ],
    children: [
      { path: '/sales', label: 'Dashboard Ventas', icon: TrendingUp },
      { path: '/sales/upload', label: 'Importar', icon: Package },
      { path: '/sales/history', label: 'Historial', icon: List },
    ],
  },
  crm_core: {
    scope: 'crm_core',
    path: '/crm',
    icon: Users,
    label: 'CRM',
    benefits: [
      'Gestioná profesionales',
      'Expedientes de clientes',
      'Turnos/calendario',
      'Reportes de visita',
      'Vinculación profesional-cliente',
      'Estados de expediente',
    ],
    children: [
      { path: '/crm/dashboard', label: 'Panel', icon: Users },
      { path: '/crm', label: 'Profesionales', icon: Users },
      { path: '/crm/services', label: 'Servicios', icon: Package },
      { path: '/crm/files', label: 'Expedientes', icon: FileText },
      { path: '/crm/appointments', label: 'Calendario', icon: Calendar },
      { path: '/crm/reports', label: 'Reportes', icon: FileText },
      { path: '/crm/reminders', label: 'Recordatorios', icon: Bell },
      { path: '/crm/blocks', label: 'Bloqueos', icon: Lock },
      { path: '/crm/waitlist', label: 'Lista de espera', icon: List },
      { path: '/crm/integrations', label: 'Integraciones', icon: Plug },
      { path: '/crm/tasks', label: 'Tareas', icon: CheckSquare },
    ],
  },
  billing: {
    scope: 'billing',
    path: '/treasury',
    icon: Wallet,
    label: 'Finanzas',
    benefits: [
      'Comprobantes fiscales (ARCA) e internos',
      'Cajas y movimientos de liquidez',
      'Saldos calculados en tiempo real',
      'Perfiles fiscales y certificados',
      'Control de gastos',
      'Análisis de rentabilidad',
    ],
    children: [
      { path: '/finance', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/treasury', label: 'Cajas', icon: Wallet },
      { path: '/billing', label: 'Comprobantes', icon: Receipt },
      { path: '/billing/issuers', label: 'Emisores', icon: Building2 },
      { path: '/finance/costs', label: 'Gastos', icon: Coins },
      { path: '/finance/receivables', label: 'Cuentas por cobrar', icon: HandCoins },
      { path: '/finance/quotes', label: 'Cotizaciones', icon: FileSignature },
      { path: '/finance/profitability', label: 'Rentabilidad', icon: TrendingUp },
      { path: '/finance/analytics', label: 'Analítica', icon: BarChart2 },
      { path: '/finance/budgets', label: 'Presupuestos', icon: Target },
      { path: '/finance/cards', label: 'Tarjetas', icon: CreditCard },
      { path: '/finance/categories', label: 'Categorías', icon: Tag },
      { path: '/finance/goals', label: 'Metas de ahorro', icon: PiggyBank },
    ],
  },
  stock: {
    scope: 'stock',
    path: '/stock',
    icon: Package,
    label: 'Stock',
    benefits: [
      'Control de inventario',
      'Gestión de productos',
      'Recetas y producción',
      'Movimientos de stock',
      'Alertas de stock bajo',
    ],
    children: [
      { path: '/stock', label: 'Dashboard Stock', icon: Package },
      { path: '/stock/materials', label: 'Materias Primas', icon: Package },
      { path: '/stock/products', label: 'Productos', icon: Package },
      { path: '/stock/recipes', label: 'Recetas', icon: FileText },
      { path: '/stock/sales', label: 'Ventas Stock', icon: TrendingUp },
      { path: '/stock/profitability', label: 'Rentabilidad', icon: TrendingUp },
      { path: '/stock/costs', label: 'Costos', icon: Coins },
      { path: '/stock/periods', label: 'Períodos', icon: Calendar },
    ],
  },
  messaging: {
    scope: 'messaging',
    path: '/kafi-bot',
    icon: Bot,
    label: 'KAFI Bot',
    benefits: [
      'Vinculación segura por WhatsApp',
      'Consultas operativas para el equipo',
      'Permisos por persona',
      'Revocación y trazabilidad',
    ],
    children: [
      { path: '/kafi/overview', label: 'Analítica', icon: BarChart2 },
      { path: '/kafi-bot', label: 'Configuración', icon: Bot },
    ],
  },
}

/**
 * Returns the module map for sidebar navigation.
 * Each entry maps a scope string to its route path, icon, and label.
 */
export function getScopeModuleMap(): Record<string, ModuleInfo> {
  return MODULE_MAP
}

/**
 * Get the list of all module entries that should appear in the sidebar.
 *
 * Returns ALL modules from MODULE_MAP regardless of JWT scopes.
 * The `subscribed` flag is set dynamically based on whether the module's
 * scope is present in the user's JWT scopes array.
 * Paywall behavior (Change 30) uses this flag to show/hide the paywall screen.
 *
 * IMPORTANT: scope values in MODULE_MAP now match backend feature_codes
 * (qr_attendance, sales_analytics, crm_core, stock) so they correctly
 * compare against the JWT scopes array.
 */
export function getActiveModules(scopes: string[]): ModuleInfo[] {
  return Object.values(MODULE_MAP).map((mod) => ({
    ...mod,
    subscribed: scopes.includes(mod.scope),
  }))
}

/**
 * Map module scopes to their permission prefixes.
 * Module scopes use underscores (qr_attendance, crm_core) but
 * permissions use colons (qr:, crm:).
 */
const SCOPE_TO_PERM_PREFIX: Record<string, string> = {
  documentation: '',
  qr_attendance: 'qr:',
  crm_core: 'crm:',
  sales_analytics: 'sales:',
  billing: 'billing:',
  stock: 'stock:',
  messaging: 'messaging:',
}

/**
 * Filtra los módulos del menú según los permisos del usuario.
 * - `admin:all` ve todo.
 * - Otros usuarios ven módulos donde tengan al menos un permiso con el prefijo del módulo.
 */
export function filterModulesForPermissions(modules: ModuleInfo[], permissions: string[]): ModuleInfo[] {
  if (permissions.includes('admin:all')) return modules
  return modules.filter((mod) => {
    const prefix = SCOPE_TO_PERM_PREFIX[mod.scope] || `${mod.scope}:`
    return permissions.some((p) => p.startsWith(prefix))
  })
}
